/* Service and information pages (sections/svc-*.liquid, sections/page.liquid).
   Everything on these pages works without this file; it adds:
   - the phone action bar, shown while the opening's button is off screen;
   - search and a current-topic marker for the FAQs;
   - the current part of the page in "On this page" and contents lists;
   - whether an office's team is in right now, in the office's own time zone;
   - opening the answer a link points to (…/pages/faqs#q-how-does-layaway-work);
   - a contents list for long text, built from its headings;
   - Calendly's height, so the booking calendar never scrolls inside itself;
   - films that play only while on screen, with a pause button. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canObserve = 'IntersectionObserver' in window;

  /* Phone action bar ------------------------------------------------------- */

  function initDock(dock) {
    if (!canObserve) return;
    const hero = dock.previousElementSibling?.closest('.svc-hero') || document.querySelector('.svc-hero');
    // On phones the opening's photograph comes first, so on a short screen its
    // button can start below the fold: the bar stands in for that button
    // whenever it isn't on screen, not only once the whole opening has gone.
    const heroButton = hero?.querySelector('.svc-actions .svc-button');
    const target = dock.dataset.svcDockTarget ? document.querySelector(dock.dataset.svcDockTarget) : null;
    const footer = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
    // The closing band carries the same action: the bar gives way to it.
    const watched = [heroButton || hero, target, footer, ...document.querySelectorAll('.svc-band')].filter(Boolean);
    const visible = new Map();
    // The bar exists only on phones held upright (the same query as in
    // service.css); elsewhere it never counts as shown.
    const phone = window.matchMedia(document.querySelector('.svc-localnav')
      ? 'screen and (max-width: 749px) and (min-height: 34.0625rem)'
      : 'screen and (max-width: 989px) and (min-height: 34.0625rem)');

    const sync = () => {
      const show = phone.matches && watched.every((element) => !visible.get(element));
      dock.classList.toggle('is-shown', show);
      // Lets chat bubbles and other fixed buttons make room for the bar.
      document.body.classList.toggle('svc-dock-open', show);
    };
    // The opening's button counts as seen only when it is wholly on screen
    // (a button cut by the screen's edge is easy to miss); the rest, as soon
    // as any of it shows.
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => visible.set(entry.target, entry.target === heroButton ? entry.intersectionRatio > 0.98 : entry.isIntersecting));
      sync();
    }, { threshold: [0, 1] });
    watched.forEach((element) => observer.observe(element));
    phone.addEventListener('change', sync);
  }

  /* FAQs: search and the current topic ------------------------------------ */

  const fold = (text) => text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const escapeHtml = (text) => text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function highlight(label, original, terms) {
    if (!terms.length) {
      label.textContent = original;
      return;
    }
    const folded = fold(original);
    const marks = [];
    terms.forEach((term) => {
      let from = 0;
      let at;
      while (term && (at = folded.indexOf(term, from)) !== -1) {
        marks.push([at, at + term.length]);
        from = at + term.length;
      }
    });
    if (!marks.length) {
      label.textContent = original;
      return;
    }
    marks.sort((a, b) => a[0] - b[0]);
    let html = '';
    let cursor = 0;
    marks.forEach(([start, end]) => {
      if (start < cursor) return;
      html += escapeHtml(original.slice(cursor, start)) + '<mark>' + escapeHtml(original.slice(start, end)) + '</mark>';
      cursor = end;
    });
    label.innerHTML = html + escapeHtml(original.slice(cursor));
  }

  function initFaq(root) {
    const input = root.querySelector('[data-svc-faq-input]');
    // Phones: once the keyboard is up, lift the search to just under the header so
    // the count and the first answers stay above the keyboard.
    const phone = window.matchMedia('(max-width: 989px)');
    const searchBox = root.querySelector('[data-svc-faq-search]') || input;
    if (input && searchBox) {
      const lift = () => {
        if (phone.matches && document.activeElement === input) searchBox.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      };
      input.addEventListener('focus', () => {
        window.visualViewport?.addEventListener('resize', lift, { once: true });
        window.setTimeout(lift, 350);
      });
    }
    const links = [...root.querySelectorAll('[data-svc-faq-link]')];
    const groups = [...root.querySelectorAll('[data-svc-faq-group]')];

    if (input) {
      const box = root.querySelector('[data-svc-faq-search]');
      const clear = root.querySelector('[data-svc-faq-clear]');
      const status = root.querySelector('[data-svc-faq-status]');
      const empty = root.querySelector('[data-svc-faq-empty]');
      const emptyQuery = root.querySelector('[data-svc-faq-query]');
      // An answer is found by its words, its topic ("spa" finds every spa answer) and the
      // words people use that it doesn't (keywords: "insurance", "how much").
      const items = [...root.querySelectorAll('[data-svc-faq-item]')].map((element) => {
        const label = element.querySelector('.svc-faq__q-text');
        const topic = element.closest('[data-svc-faq-group]')?.querySelector('.svc-faq__group-title')?.textContent || '';
        return { element, label, question: label.textContent, text: fold([element.textContent, element.dataset.svcFaqKeywords || '', topic].join(' ')) };
      });
      const openedBySearch = new Set();
      // Answers someone closed during this search stay closed as they type on.
      const closedByUser = new Set();
      items.forEach(({ element }) => {
        element.addEventListener('toggle', () => {
          if (element.open) {
            closedByUser.delete(element);
          } else if (openedBySearch.has(element)) {
            openedBySearch.delete(element);
            closedByUser.add(element);
          }
        });
      });
      let timer = 0;

      const run = () => {
        const raw = input.value.trim();
        // Plurals match their singular ("returns" finds "a return").
        const terms = fold(raw)
          .split(/\s+/)
          .filter(Boolean)
          .map((term) => (term.length > 4 && term.endsWith('s') ? term.slice(0, -1) : term));
        box?.classList.toggle('has-value', raw.length > 0);
        root.classList.toggle('is-searching', terms.length > 0);

        let shown = 0;
        items.forEach((item) => {
          const match = terms.every((term) => item.text.includes(term));
          item.element.hidden = !match;
          highlight(item.label, item.question, match ? terms : []);
          if (match) shown += 1;
        });

        // Open the answers when a search narrows things down to a few; close
        // the ones the search opened once they no longer match, once the
        // search widens again, or once it is cleared.
        openedBySearch.forEach((element) => {
          if (!terms.length || element.hidden || shown > 4) {
            element.open = false;
            openedBySearch.delete(element);
          }
        });
        if (!terms.length) closedByUser.clear();
        if (terms.length && shown > 0 && shown <= 4) {
          items.forEach((item) => {
            if (!item.element.hidden && !item.element.open && !closedByUser.has(item.element)) {
              item.element.open = true;
              openedBySearch.add(item.element);
            }
          });
        }

        groups.forEach((group) => {
          group.hidden = !group.querySelector('[data-svc-faq-item]:not([hidden])');
        });
        links.forEach((link) => {
          const group = document.getElementById(decodeURIComponent(link.hash.slice(1)));
          link.parentElement.hidden = Boolean(group && group.hidden);
          const count = link.querySelector('[data-svc-faq-count]');
          if (count && group) {
            count.dataset.total = count.dataset.total || count.textContent;
            count.textContent = terms.length ? group.querySelectorAll('[data-svc-faq-item]:not([hidden])').length : count.dataset.total;
          }
        });
        const index = root.querySelector('.svc-faq__index');
        if (index) index.hidden = links.length > 0 && links.every((link) => link.parentElement.hidden);

        if (empty) {
          empty.hidden = !(terms.length && shown === 0);
          if (emptyQuery) emptyQuery.textContent = raw;
        }
        if (status) {
          // The empty state says it on screen; the status line stays for screen readers.
          status.classList.toggle('sr-only', Boolean(empty && terms.length && shown === 0));
          status.textContent = !terms.length
            ? ''
            : shown === 0
              ? 'No answers match your search.'
              : `${shown} ${shown === 1 ? 'answer matches' : 'answers match'} your search.`;
        }
      };

      const suggestions = [...root.querySelectorAll('[data-svc-faq-suggest]')];
      const markSuggestion = () => {
        const value = fold(input.value.trim());
        suggestions.forEach((button) => button.setAttribute('aria-pressed', String(fold(button.dataset.svcFaqSuggest) === value)));
      };
      suggestions.forEach((button) => {
        button.addEventListener('click', () => {
          const same = button.getAttribute('aria-pressed') === 'true';
          input.value = same ? '' : button.dataset.svcFaqSuggest;
          run();
          markSuggestion();
        });
      });

      // Example searches take turns in the empty box (once, slowly; never
      // while someone is typing, and not at all for reduced motion).
      const examples = (input.dataset.svcFaqExamples || '').split('|').map((item) => item.trim()).filter(Boolean);
      if (examples.length) {
        const base = input.placeholder;
        let turn = 0;
        const show = () => {
          if (document.activeElement === input || input.value) return;
          input.placeholder = `Try “${examples[turn % examples.length]}”`;
          turn += 1;
        };
        show();
        if (!reduceMotion.matches && examples.length > 1) {
          const rotation = setInterval(() => {
            if (turn > examples.length * 3) {
              clearInterval(rotation);
              input.placeholder = base;
              return;
            }
            show();
          }, 2800);
        }
      }

      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          run();
          markSuggestion();
        }, 90);
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && input.value) {
          event.preventDefault();
          input.value = '';
          run();
          markSuggestion();
        }
      });
      clear?.addEventListener('click', () => {
        input.value = '';
        run();
        markSuggestion();
        input.focus();
      });
    }

    if (links.length && canObserve) spy(links);
  }

  /* The link to the part of the page being read (FAQ topics, contents) ---- */

  function spy(links) {
    const pairs = links
      .map((link) => ({ link, target: document.getElementById(decodeURIComponent(link.hash.slice(1))) }))
      .filter((pair) => pair.target);
    if (!pairs.length) return;
    const bar = pairs[0].link.closest('.svc-localnav__links, .svc-faq__index');
    let current = null;
    let line = 0;
    let observer = null;

    // The current part is the last one whose top has passed a reading line
    // just under where a link lands it, so a click marks what was clicked and
    // parts between the linked ones keep the one before them.
    const mark = () => {
      let next = null;
      let best = -Infinity;
      // The sell bar: past the last part (at the closing band and the cards after it), none.
      const band = bar && document.querySelector('.svc-band');
      const past = Boolean(band) && band.getBoundingClientRect().top <= line + 1;
      pairs.forEach((pair) => {
        if (!pair.target.getClientRects().length) return;
        // At or above the band's lower edge: a part entering the band counts.
        const top = pair.target.getBoundingClientRect().top;
        if (top <= line + 1 && top > best) {
          best = top;
          next = pair;
        }
      });
      if (past) next = null;
      if (next === current) return;
      current = next;
      pairs.forEach((pair) => {
        if (pair === current) pair.link.setAttribute('aria-current', 'true');
        else pair.link.removeAttribute('aria-current');
      });
      const list = current && current.link.closest('.svc-legal__toc');
      if (list && list.scrollHeight > list.clientHeight + 1) {
        const link = current.link.getBoundingClientRect();
        const box = list.getBoundingClientRect();
        if (link.top < box.top + 48 || link.bottom > box.bottom - 48) {
          list.scrollTo({ top: list.scrollTop + (link.top - box.top) - box.height / 3, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      }
      if (current && bar && bar.contains(current.link) && bar.scrollWidth > bar.clientWidth) {
        const link = current.link.getBoundingClientRect();
        const box = bar.getBoundingClientRect();
        if (link.left < box.left || link.right > box.right) {
          // Keep the row's own start padding (the FAQ topics use the page margin).
          const inset = parseFloat(getComputedStyle(bar).scrollPaddingInlineStart) || 16;
          bar.scrollBy({ left: link.left - box.left - inset, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      }
    };

    // A one-pixel band on the reading line. Sections are watched as well:
    // they tile the page, so crossing into a part with no link of its own
    // still updates the marker.
    const watch = () => {
      const header = parseFloat(document.documentElement.style.getPropertyValue('--header-height')) || 88;
      const landing = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || header + 20;
      line = Math.round(landing + 40);
      observer?.disconnect();
      observer = new IntersectionObserver(mark, { rootMargin: `-${line}px 0px -${Math.max(0, window.innerHeight - line - 1)}px 0px` });
      pairs.forEach((pair) => observer.observe(pair.target));
      document.querySelectorAll('#MainContent > .shopify-section').forEach((wrapper) => observer.observe(wrapper));
    };
    watch();
    let resizing;
    window.addEventListener('resize', () => {
      clearTimeout(resizing);
      resizing = setTimeout(watch, 200);
    });
  }

  /* Open the answer (or section) the address points to --------------------- */

  function openFromHash() {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    const details = target.matches('details') ? target : target.closest('details');
    if (details && !details.open) {
      details.open = true;
      requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }
  }

  /* Long text from the admin: one H1 per page -------------------------------- */

  function demoteHeadings(body) {
    body.querySelectorAll('h1').forEach((h1) => {
      const h2 = document.createElement('h2');
      [...h1.attributes].forEach((attribute) => h2.setAttribute(attribute.name, attribute.value));
      h2.append(...h1.childNodes);
      h1.replaceWith(h2);
    });
  }

  /* Contents for long text ------------------------------------------------- */

  function initToc(nav) {
    const body = nav.parentElement.querySelector('[data-svc-doc]');
    if (!body) return;
    // Headings that are really sentences ("Please feel free to contact us…")
    // don't belong in a contents list.
    const short = (heading) => heading.textContent.trim().length <= 48;
    let headings = [...body.querySelectorAll('h2')].filter(short);
    if (headings.length < 3) headings = [...body.querySelectorAll('h2, h3')].filter(short);
    // The condition guide: its grades are the contents ("Pristine", not "Pristine Brand new").
    let labels = null;
    if (headings.length < 3) {
      const tiers = [...body.querySelectorAll('.cg-tier')].filter((tier) => tier.querySelector('.cg-tier-name'));
      if (tiers.length >= 3) {
        headings = tiers;
        labels = new Map(tiers.map((tier) => {
          const name = tier.querySelector('.cg-tier-name');
          const first = [...name.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
          return [tier, (first ? first.textContent : name.textContent).trim()];
        }));
      }
    }
    if (headings.length < 3) return;
    const list = nav.querySelector('ol');
    const used = new Set();
    headings.forEach((heading) => {
      if (!heading.id) {
        let slug = fold(heading.textContent).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'section';
        while (used.has(slug) || document.getElementById(slug)) slug += '-';
        heading.id = slug;
      }
      used.add(heading.id);
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      const label = labels?.get(heading) || heading.textContent.trim();
      link.textContent = /[A-Z]/.test(label) && label === label.toUpperCase() ? label.charAt(0) + label.slice(1).toLowerCase() : label;
      item.append(link);
      list.append(item);
    });
    nav.hidden = false;
    if (canObserve) spy([...list.querySelectorAll('a')]);
  }

  /* Floating pieces: a little depth under a real pointer ------------------ */

  function initPieces(hero) {
    if (reduceMotion.matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    let frame = 0;
    hero.addEventListener('pointermove', (event) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const box = hero.getBoundingClientRect();
        const x = ((event.clientX - box.left) / box.width) * 2 - 1;
        const y = ((event.clientY - box.top) / box.height) * 2 - 1;
        hero.style.setProperty('--mx', (-x).toFixed(3));
        hero.style.setProperty('--my', (-y).toFixed(3));
      });
    });
    hero.addEventListener('pointerleave', () => {
      hero.style.setProperty('--mx', '0');
      hero.style.setProperty('--my', '0');
    });
  }

  /* Policies: the contents list folds away on phones ----------------------- */

  function initLegalToc(toc) {
    const phone = window.matchMedia('(max-width: 989px)');
    const summary = toc.querySelector('summary');
    // On larger screens the list is always open, so its toggle is no stop.
    const sync = () => {
      toc.open = !phone.matches;
      if (summary) summary.tabIndex = phone.matches ? 0 : -1;
    };
    sync();
    phone.addEventListener('change', sync);
    // A list taller than the screen scrolls inside itself: it fades at the
    // bottom so it reads as more (service.css).
    const measure = () => toc.classList.toggle('is-scrollable', !phone.matches && toc.scrollHeight > toc.clientHeight + 1);
    measure();
    let resizing = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizing);
      resizing = setTimeout(measure, 150);
    });
    toc.addEventListener('toggle', () => {
      if (!phone.matches && !toc.open) toc.open = true;
    });
    toc.addEventListener('click', (event) => {
      if (phone.matches && event.target.closest('a')) toc.open = false;
    });
  }

  /* Embedded tools (Consignr, Calendly): after the opening's photograph ----- */

  function initEmbeds() {
    const frames = [...document.querySelectorAll('iframe[data-src]')];
    if (!frames.length) return;
    const load = (frame) => {
      if (frame.getAttribute('src')) return;
      // The box behind the frame says it's loading until the page inside has
      // arrived. Calendly draws its calendar after its page loads: its first
      // message says it's there (initCalendly), with a timer in case it never
      // speaks.
      const loaded = () => frame.closest('.svc-form__frame-box')?.classList.add('is-loaded');
      frame.addEventListener('load', () => {
        if (frame.hasAttribute('data-svc-calendly')) setTimeout(loaded, 6000);
        else loaded();
      }, { once: true });
      frame.src = frame.dataset.src;
    };
    const start = () => {
      if (!canObserve) {
        frames.forEach(load);
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              load(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '600px 0px' }
      );
      frames.forEach((frame) => observer.observe(frame));
    };
    // Let the opening's photograph (what the page is measured by) arrive first,
    // without waiting for every tag on the page.
    let started = false;
    const go = () => {
      if (started) return;
      started = true;
      start();
    };
    const hero = document.querySelector('.svc-hero__media img');
    if (hero && !hero.complete) {
      hero.addEventListener('load', go, { once: true });
      hero.addEventListener('error', go, { once: true });
      setTimeout(go, 4000);
    } else {
      go();
    }
    // At once when someone asks for it (the opening's button, the phone bar).
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      const target = link && link.hash.length > 1 && document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (target) target.querySelectorAll('iframe[data-src]').forEach(load);
    });
    // And when the page is opened at the tool's own address (…#book).
    const hashTarget = window.location.hash.length > 1 && document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
    if (hashTarget) hashTarget.querySelectorAll('iframe[data-src]').forEach(load);
  }

  /* Calendly: follow the calendar's height --------------------------------- */

  function initCalendly(frames) {
    // Grow freely. Shrink only while the frame's bottom is below the screen,
    // so nothing in view jumps up.
    window.addEventListener('message', (event) => {
      if (event.origin !== 'https://calendly.com') return;
      const data = event.data;
      if (data && typeof data.event === 'string' && data.event.startsWith('calendly.')) {
        frames.forEach((frame) => {
          if (frame.contentWindow === event.source) frame.closest('.svc-form__frame-box')?.classList.add('is-loaded');
        });
      }
      if (!data || data.event !== 'calendly.page_height') return;
      const height = parseInt(data.payload && data.payload.height, 10);
      // While loading, Calendly posts heights of a few pixels; ignore them so
      // the reserved space doesn't collapse and jump back.
      if (!height || height < 480) return;
      frames.forEach((frame) => {
        if (frame.contentWindow !== event.source) return;
        const box = frame.getBoundingClientRect();
        if (height > box.height || box.bottom > window.innerHeight) frame.style.height = `${height}px`;
      });
    });
  }

  /* Keep the theme's back-to-top button off forms on phones ---------------- */

  // The opening's button and the closing band carry the same action: the sell
  // bar's button steps aside while either is on screen (one filled button at a time).
  function initBandWatch() {
    if (!canObserve || !document.querySelector('.svc-localnav')) return;
    const watch = (elements, className) => {
      if (!elements.length) return;
      const onScreen = new Set();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? onScreen.add(entry.target) : onScreen.delete(entry.target)));
        document.body.classList.toggle(className, onScreen.size > 0);
      });
      elements.forEach((element) => observer.observe(element));
    };
    watch([...document.querySelectorAll('.svc-band')], 'svc-band-in-view');
    watch([...document.querySelectorAll('.svc-hero .svc-button')], 'svc-hero-action-in-view');
  }

  function initFormWatch() {
    const panels = [...document.querySelectorAll('.svc-form__panel')];
    if (!panels.length || !canObserve) return;
    const onScreen = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? onScreen.add(entry.target) : onScreen.delete(entry.target)));
      document.body.classList.toggle('svc-form-in-view', onScreen.size > 0);
    });
    panels.forEach((panel) => observer.observe(panel));
  }

  /* Offices: in right now, or when the team is back (in the office's zone) */

  function initOpenStatus() {
    const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const time = (minutes) => {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      return `${((hour + 11) % 12) + 1}${minute ? `:${String(minute).padStart(2, '0')}` : ''}${hour < 12 ? 'am' : 'pm'}`;
    };
    const longDate = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
    document.querySelectorAll('[data-svc-open]').forEach((element) => {
      const match = /^(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) (\d\d):(\d\d)-(\d\d):(\d\d)$/.exec(element.dataset.hours || '');
      if (!match) return;
      const from = days.indexOf(match[1]);
      const to = days.indexOf(match[2]);
      const opens = Number(match[3]) * 60 + Number(match[4]);
      const closes = Number(match[5]) * 60 + Number(match[6]);
      // "PT" and "MT" hold across daylight saving; "PDT" where they're unknown.
      const clock = (timeZoneName) => new Intl.DateTimeFormat('en-US', { timeZone: element.dataset.tz, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23', timeZoneName }).formatToParts(new Date());
      let parts;
      try {
        parts = clock('shortGeneric');
      } catch (error) {
        try {
          parts = clock('short');
        } catch (fallbackError) {
          return;
        }
      }
      const part = (type) => (parts.find((item) => item.type === type) || {}).value || '';
      const today = days.indexOf(part('weekday').slice(0, 2));
      if (today < 0) return;
      const now = Number(part('hour')) * 60 + Number(part('minute'));
      const zone = part('timeZoneName');
      // Today's date in the office, and the date a number of days ahead.
      const base = Date.UTC(Number(part('year')), Number(part('month')) - 1, Number(part('day')));
      const dateAhead = (ahead) => new Date(base + ahead * 86400000);
      const closed = new Set((element.dataset.closed || '').split(/[\s,]+/).filter(Boolean));
      const isWorkday = (day, ahead) => (from <= to ? day >= from && day <= to : day >= from || day <= to) && !closed.has(dateAhead(ahead).toISOString().slice(0, 10));

      const open = isWorkday(today, 0) && now >= opens && now < closes;
      element.dataset.state = open ? 'open' : 'closed';
      if (open) {
        element.textContent = `Open now, until ${time(closes)} ${zone}`;
      } else {
        let day = today;
        let ahead = 0;
        if (!(isWorkday(today, 0) && now < opens)) {
          do {
            day = (day + 1) % 7;
            ahead += 1;
          } while (!isWorkday(day, ahead) && ahead < 21);
        }
        if (ahead >= 21) return;
        const when = ahead === 0 ? 'today' : ahead === 1 ? 'tomorrow' : ahead < 7 ? names[day] : `on ${longDate.format(dateAhead(ahead))}`;
        element.textContent = `Opens ${when} at ${time(opens)} ${zone}`;
      }
      element.hidden = false;
    });
  }

  /* Films that wait to be played, with sound ------------------------------ */

  function initFilm(button) {
    const wrap = button.closest('.svc-split__video');
    const video = wrap && wrap.querySelector('video[data-svc-film]');
    if (!video) return;
    video.controls = false;
    if (canObserve) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !video.paused) video.pause();
        });
      }).observe(wrap);
    }
    button.addEventListener('click', () => {
      video.controls = true;
      video.muted = false;
      wrap.classList.add('is-playing');
      video.play().catch(() => {});
      video.focus({ preventScroll: true });
    });
  }

  /* Films ----------------------------------------------------------------- */

  function initVideo(wrap) {
    const video = wrap.querySelector('video');
    const toggle = wrap.querySelector('[data-svc-video-toggle]');
    if (!video) return;
    let userPaused = reduceMotion.matches;
    const setPaused = (paused) => {
      wrap.classList.toggle('is-paused', paused);
      toggle?.setAttribute('aria-label', paused ? 'Play the film' : 'Pause the film');
    };
    setPaused(true);
    toggle?.addEventListener('click', () => {
      if (video.paused) {
        userPaused = false;
        video.play().catch(() => {});
      } else {
        userPaused = true;
        video.pause();
      }
    });
    video.addEventListener('play', () => setPaused(false));
    video.addEventListener('pause', () => setPaused(true));
    if (!canObserve) return;
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !userPaused) video.play().catch(() => {});
          else if (!entry.isIntersecting && !video.paused) video.pause();
        });
      },
      { threshold: 0.35 }
    ).observe(wrap);
  }

  /* Links that point at the page they're on --------------------------------- */

  function tidyLinks() {
    const form = document.querySelector('.svc-form[id]');
    document.querySelectorAll('.svc a[href]').forEach((link) => {
      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Admin text sometimes opens this site's own pages in new tabs.
      if (link.closest('[data-svc-doc]') && link.target === '_blank') {
        link.removeAttribute('target');
        link.querySelectorAll('.sr-only').forEach((note) => note.remove());
      }
      // A link to the page itself (from a shared FAQ answer) jumps to its form.
      if (form && url.pathname === window.location.pathname && !url.hash) link.setAttribute('href', `#${form.id}`);
    });
  }

  /* Lists styled without bullets keep their list semantics (Safari) --------- */

  function keepLists() {
    document.querySelectorAll('.svc-rte :is(ul, ol):not([role])').forEach((list) => list.setAttribute('role', 'list'));
  }

  /* Sideways rows: reachable by keyboard while they scroll ------------------ */

  function initScrollers() {
    const rows = [...document.querySelectorAll('[data-svc-scroller]')];
    if (!rows.length) return;
    const sync = () => {
      rows.forEach((row) => {
        // A row that scrolls needs a Tab stop of its own to be scrolled by
        // keyboard, unless it already holds links (Tab then scrolls it).
        const reachable = row.querySelector('a[href], button, input, [tabindex]:not([tabindex="-1"]):not([data-svc-scroller])');
        if (row.scrollWidth > row.clientWidth + 1 && !reachable) row.setAttribute('tabindex', '0');
        else row.removeAttribute('tabindex');
      });
    };
    sync();
    let timer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(timer);
      timer = setTimeout(sync, 150);
    });
  }

  /* Start ------------------------------------------------------------------ */

  document.querySelectorAll('[data-svc-dock]').forEach(initDock);
  document.querySelectorAll('[data-svc-faq]').forEach(initFaq);
  document.querySelectorAll('[data-svc-doc]').forEach(demoteHeadings);
  document.querySelectorAll('[data-svc-toc]').forEach(initToc);
  document.querySelectorAll('[data-svc-film-play]').forEach(initFilm);
  document.querySelectorAll('[data-svc-video]').forEach(initVideo);
  document.querySelectorAll('[data-svc-legal-toc]').forEach(initLegalToc);
  document.querySelectorAll('[data-svc-pieces]').forEach(initPieces);
  if (canObserve) document.querySelectorAll('[data-svc-spy]').forEach((list) => spy([...list.querySelectorAll('a[href^="#"]')]));
  initEmbeds();
  initFormWatch();
  initBandWatch();
  initOpenStatus();
  const calendly = [...document.querySelectorAll('[data-svc-calendly]')];
  if (calendly.length) initCalendly(calendly);

  tidyLinks();
  keepLists();
  initScrollers();
  openFromHash();
  window.addEventListener('hashchange', openFromHash);
})();
